import { useMemo, useRef } from 'react';
import { useRequiredVideoProgress } from './use-required-video-progress';
import {
  useFireOnce,
  useFlushOnComplete,
  useOnTime,
  useProgressRef,
  useReadyOnce,
} from './use-required-video-helpers';
import { deriveCompleted, deriveRemaining } from './use-required-video-pure';
import type {
  RequiredVideoConfig,
  SeekFn,
  VideoProgress,
} from '@/types/required-video';

type Args = {
  config: RequiredVideoConfig;
  mode: 'server' | 'local';
  onWatched: () => void;
};

export type RequiredVideoState = {
  progress: VideoProgress;
  completed: boolean;
  remainingSecs: number;
  progressLoading: boolean;
  onTime: (t: number, d: number) => void;
  onReady: (seek: SeekFn) => void;
};

export function useRequiredVideo(args: Args): RequiredVideoState {
  const { config, mode, onWatched } = args;
  const { progress, setProgress, flush, progressLoading } =
    useRequiredVideoProgress({ videoKey: config.video_key, mode });
  const lastTRef = useRef(-1);
  const progressRef = useProgressRef(progress);
  const completed = deriveCompleted(progress, config);
  useFireOnce(completed, onWatched);
  useFlushOnComplete(completed, flush);
  const onReady = useReadyOnce(progressRef);
  const timeArgs = useMemo(
    () => ({ lastTRef, progressRef, setProgress }),
    [progressRef, setProgress]
  );
  const onTime = useOnTime(timeArgs);
  const remaining = deriveRemaining(progress, config, completed);
  return {
    progress,
    completed,
    remainingSecs: remaining,
    progressLoading,
    onTime,
    onReady,
  };
}
